import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Badge,
  Button,
  Card,
  EmptyState,
  Field,
  Icon,
  IconBtn,
  Input,
  Modal,
  ModalBody,
  ModalFooter,
  PageHeader,
  Select,
  Spinner,
  useToast,
  useConfirm,
} from "../../design-system";
import {
  createHoliday,
  createHolidayGroup,
  deleteHoliday,
  deleteHolidayGroup,
  getHolidayGroups,
  getHolidaysByGroup,
  updateHolidayGroup,
} from "../../helpers/kronaly_helper";
import { SPANISH_PRESETS, SUPPORTED_YEARS } from "../../helpers/spanish_holidays";

const KIND_OPTIONS = [
  { value: "national", label: "Nacional" },
  { value: "regional", label: "Autonómico" },
  { value: "local", label: "Local" },
  { value: "other", label: "Otro" },
];

const KIND_LABEL = {
  national: "Nacional",
  regional: "Autonómico",
  local: "Local",
  other: "Otro",
};

const KIND_TONE = {
  national: "info",
  regional: "brand",
  local: "warning",
  other: "neutral",
};

const unwrap = (r) => {
  const inner = r?.data ?? r;
  return inner?.data ?? inner;
};

const fmtDateLong = (ymd) => {
  if (!ymd) return "—";
  const d = new Date(`${ymd}T00:00:00`);
  if (Number.isNaN(d.getTime())) return ymd;
  return d.toLocaleDateString("es-ES", {
    weekday: "short",
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
};

function Festivos() {
  document.title = "Festivos | Kronaly";
  const toast = useToast();
  const confirm = useConfirm();

  const [groups, setGroups] = useState([]);
  const [loadingGroups, setLoadingGroups] = useState(true);
  const [groupsError, setGroupsError] = useState(null);

  const [activeId, setActiveId] = useState(null);
  const [holidays, setHolidays] = useState([]);
  const [loadingHolidays, setLoadingHolidays] = useState(false);
  const [holidaysError, setHolidaysError] = useState(null);

  const [showGroupModal, setShowGroupModal] = useState(false);
  const [editingGroup, setEditingGroup] = useState(null);
  const [showHolidayModal, setShowHolidayModal] = useState(false);
  const [showImportModal, setShowImportModal] = useState(false);

  const activeGroup = useMemo(
    () => groups.find((g) => g.id === activeId) || null,
    [groups, activeId],
  );

  const loadGroups = useCallback(async () => {
    setLoadingGroups(true);
    setGroupsError(null);
    try {
      const r = await getHolidayGroups();
      const list = unwrap(r) || [];
      setGroups(Array.isArray(list) ? list : []);
      if (Array.isArray(list) && list.length > 0 && !activeId) {
        setActiveId(list[0].id);
      }
    } catch (e) {
      setGroupsError(typeof e === "string" ? e : e?.message || "No se pudieron cargar los grupos.");
    } finally {
      setLoadingGroups(false);
    }
  }, [activeId]);

  const loadHolidays = useCallback(async (groupId) => {
    if (!groupId) {
      setHolidays([]);
      return;
    }
    setLoadingHolidays(true);
    setHolidaysError(null);
    try {
      const r = await getHolidaysByGroup(groupId);
      const list = unwrap(r) || [];
      setHolidays(Array.isArray(list) ? list : []);
    } catch (e) {
      setHolidays([]);
      setHolidaysError(typeof e === "string" ? e : e?.message || "No se pudieron cargar los festivos.");
    } finally {
      setLoadingHolidays(false);
    }
  }, []);

  useEffect(() => {
    loadGroups();
  }, [loadGroups]);

  useEffect(() => {
    if (activeId) loadHolidays(activeId);
    else setHolidays([]);
  }, [activeId, loadHolidays]);

  const onGroupSaved = (group, isNew) => {
    if (isNew) {
      setGroups((g) => [...g, group]);
      setActiveId(group.id);
    } else {
      setGroups((g) => g.map((x) => (x.id === group.id ? group : x)));
    }
  };

  const onDeleteGroup = async (group) => {
    const ok = await confirm({
      title: "Eliminar grupo",
      description: `¿Seguro que quieres eliminar el grupo "${group.name}"? Esta acción no se puede deshacer.`,
      confirmLabel: "Eliminar",
      tone: "danger",
    });
    if (!ok) return;
    try {
      await deleteHolidayGroup(group.id);
      setGroups((g) => g.filter((x) => x.id !== group.id));
      if (activeId === group.id) {
        setActiveId(null);
      }
      toast.success("Grupo eliminado", group.name);
    } catch (e) {
      const msg = typeof e === "string" ? e : e?.message || "No se pudo eliminar el grupo.";
      toast.error("No se pudo eliminar", msg);
    }
  };

  const onHolidayAdded = (holiday) => {
    setHolidays((h) =>
      [...h, holiday].sort((a, b) => a.date.localeCompare(b.date)),
    );
  };

  const onDeleteHoliday = async (holiday) => {
    try {
      await deleteHoliday(holiday.id);
      setHolidays((h) => h.filter((x) => x.id !== holiday.id));
      toast.success("Festivo eliminado", holiday.name);
    } catch (e) {
      const msg = typeof e === "string" ? e : e?.message || "No se pudo eliminar el festivo.";
      toast.error("No se pudo eliminar", msg);
    }
  };

  return (
    <div className="kr-page">
      <PageHeader
        eyebrow="Configuración"
        title="Festivos"
        description="Grupos de festivos asignables a los contratos por país, comunidad autónoma o localidad."
        actions={
          <>
            <Button variant="secondary" icon="Download" onClick={() => setShowImportModal(true)}>
              Importar oficial
            </Button>
            <Button variant="primary" icon="Plus" onClick={() => { setEditingGroup(null); setShowGroupModal(true); }}>
              Nuevo grupo
            </Button>
          </>
        }
      />

      {groupsError && <Alert tone="danger">{groupsError}</Alert>}

      <div style={{ display: "grid", gridTemplateColumns: "320px 1fr", gap: 16, alignItems: "start" }}>
        <Card padded={false}>
          <div
            style={{
              padding: "12px 16px",
              borderBottom: "1px solid var(--kr-border-subtle)",
              fontSize: 11,
              textTransform: "uppercase",
              letterSpacing: "0.04em",
              color: "var(--kr-fg-muted)",
              fontWeight: 600,
            }}
          >
            Grupos · {groups.length}
          </div>
          {loadingGroups ? (
            <div style={{ padding: 24, textAlign: "center" }}>
              <Spinner size={16} />
            </div>
          ) : groups.length === 0 ? (
            <EmptyState
              icon="Calendar"
              title="Aún no hay grupos"
              description="Crea el primero para empezar a asignar festivos a contratos."
              action={{ label: "Nuevo grupo", icon: "Plus", onClick: () => setShowGroupModal(true) }}
            />
          ) : (
            groups.map((g) => (
              <button
                type="button"
                key={g.id}
                onClick={() => setActiveId(g.id)}
                style={{
                  display: "block",
                  width: "100%",
                  textAlign: "left",
                  padding: "12px 16px",
                  borderBottom: "1px solid var(--kr-border-subtle)",
                  background: activeId === g.id ? "var(--kr-action-soft-bg)" : "#fff",
                  borderLeft: activeId === g.id ? "3px solid var(--kr-action)" : "3px solid transparent",
                  cursor: "pointer",
                  border: "none",
                  borderRight: "none",
                }}
              >
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 8 }}>
                  <div style={{ minWidth: 0 }}>
                    <div
                      style={{
                        fontSize: 13,
                        fontWeight: 600,
                        color: "var(--kr-fg-strong)",
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                      }}
                    >
                      {g.name}
                    </div>
                  </div>
                  <Badge tone={KIND_TONE[g.kind] || "neutral"}>
                    {KIND_LABEL[g.kind] || g.kind}
                  </Badge>
                </div>
              </button>
            ))
          )}
        </Card>

        <Card padded={false}>
          {!activeGroup ? (
            <EmptyState
              icon="Calendar"
              title="Selecciona un grupo"
              description="Elige un grupo en la columna de la izquierda para ver y gestionar sus festivos."
              style={{ padding: "60px 24px" }}
            />
          ) : (
            <>
              <div
                style={{
                  padding: "16px 20px",
                  borderBottom: "1px solid var(--kr-border-subtle)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  gap: 12,
                }}
              >
                <div style={{ display: "flex", alignItems: "center", gap: 12, minWidth: 0 }}>
                  <div style={{ minWidth: 0 }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                      <span
                        style={{
                          fontSize: 16,
                          fontWeight: 700,
                          color: "var(--kr-fg-strong)",
                        }}
                      >
                        {activeGroup.name}
                      </span>
                      <Badge tone={KIND_TONE[activeGroup.kind] || "neutral"}>
                        {KIND_LABEL[activeGroup.kind] || activeGroup.kind}
                      </Badge>
                    </div>
                    <div style={{ fontSize: 12, color: "var(--kr-fg-muted)", marginTop: 2 }}>
                      {holidays.length} festivos
                    </div>
                  </div>
                </div>
                <div style={{ display: "flex", gap: 8 }}>
                  <Button
                    variant="secondary"
                    size="sm"
                    icon="Pencil"
                    onClick={() => { setEditingGroup(activeGroup); setShowGroupModal(true); }}
                  >
                    Editar
                  </Button>
                  <Button
                    variant="dangerSoft"
                    size="sm"
                    icon="Trash2"
                    onClick={() => onDeleteGroup(activeGroup)}
                  >
                    Eliminar
                  </Button>
                  <Button
                    variant="primary"
                    size="sm"
                    icon="Plus"
                    onClick={() => setShowHolidayModal(true)}
                  >
                    Añadir festivo
                  </Button>
                </div>
              </div>

              {holidaysError && (
                <div style={{ padding: "12px 20px" }}>
                  <Alert tone="danger">{holidaysError}</Alert>
                </div>
              )}

              {loadingHolidays ? (
                <div style={{ padding: 32, textAlign: "center" }}>
                  <Spinner size={16} />
                </div>
              ) : holidays.length === 0 ? (
                <EmptyState
                  icon="Calendar"
                  title="Sin festivos en este grupo"
                  description="Añade el primer festivo para empezar."
                  action={{ label: "Añadir festivo", icon: "Plus", onClick: () => setShowHolidayModal(true) }}
                />
              ) : (
                <>
                  <div
                    style={{
                      display: "grid",
                      gridTemplateColumns: "180px 1fr 80px",
                      padding: "10px 20px",
                      background: "var(--kr-neutral-50)",
                      borderBottom: "1px solid var(--kr-border)",
                      fontSize: 11,
                      textTransform: "uppercase",
                      letterSpacing: "0.04em",
                      fontWeight: 600,
                      color: "var(--kr-fg-muted)",
                    }}
                  >
                    <span>Fecha</span>
                    <span>Festivo</span>
                    <span style={{ textAlign: "right" }}>Acciones</span>
                  </div>
                  {holidays.map((h, i) => (
                    <div
                      key={h.id}
                      style={{
                        display: "grid",
                        gridTemplateColumns: "180px 1fr 80px",
                        gap: 12,
                        alignItems: "center",
                        padding: "12px 20px",
                        borderBottom:
                          i === holidays.length - 1
                            ? "none"
                            : "1px solid var(--kr-border-subtle)",
                        fontSize: 13,
                      }}
                    >
                      <div
                        style={{
                          fontFamily: "var(--kr-font-mono)",
                          color: "var(--kr-fg-strong)",
                          fontWeight: 600,
                        }}
                      >
                        {fmtDateLong(h.date)}
                      </div>
                      <div style={{ color: "var(--kr-fg-strong)" }}>{h.name}</div>
                      <div style={{ display: "flex", justifyContent: "flex-end" }}>
                        <IconBtn icon="Trash2" onClick={() => onDeleteHoliday(h)} />
                      </div>
                    </div>
                  ))}
                </>
              )}
            </>
          )}
        </Card>
      </div>

      <GroupModal
        open={showGroupModal}
        editing={editingGroup}
        onClose={() => { setShowGroupModal(false); setEditingGroup(null); }}
        onSaved={(g, isNew) => {
          onGroupSaved(g, isNew);
          setShowGroupModal(false);
          setEditingGroup(null);
          toast.success(isNew ? "Grupo creado" : "Grupo actualizado", g.name);
        }}
      />

      <HolidayModal
        open={showHolidayModal}
        groupId={activeGroup?.id || null}
        onClose={() => setShowHolidayModal(false)}
        onCreated={(h) => {
          onHolidayAdded(h);
          setShowHolidayModal(false);
          toast.success("Festivo añadido", h.name);
        }}
      />

      <ImportOfficialModal
        open={showImportModal}
        onClose={() => setShowImportModal(false)}
        onImported={(group) => {
          setGroups((g) => [...g, group]);
          setActiveId(group.id);
          setShowImportModal(false);
        }}
      />
    </div>
  );
}

function GroupModal({ open, editing, onClose, onSaved }) {
  const isEditing = Boolean(editing);
  const [name, setName] = useState("");
  const [kind, setKind] = useState("national");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (open) {
      setName(editing?.name || "");
      setKind(editing?.kind || "national");
      setError(null);
    }
  }, [open, editing]);

  const onSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError(null);
    try {
      const payload = { name: name.trim(), kind };
      let saved;
      if (isEditing) {
        const r = await updateHolidayGroup(editing.id, payload);
        saved = unwrap(r);
        onSaved(saved, false);
      } else {
        const r = await createHolidayGroup(payload);
        saved = unwrap(r);
        onSaved(saved, true);
      }
    } catch (err) {
      setError(typeof err === "string" ? err : err?.message || "No se pudo guardar el grupo.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={isEditing ? "Editar grupo" : "Nuevo grupo de festivos"}
      size="md"
    >
      <form onSubmit={onSubmit}>
        <ModalBody>
          {error && (
            <div style={{ marginBottom: 12 }}>
              <Alert tone="danger">{error}</Alert>
            </div>
          )}
          <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
            <Field label="Nombre" required>
              <Input
                value={name}
                onChange={(e) => setName(e.target.value)}
                maxLength={255}
                required
                autoFocus
                placeholder="Ej. Festivos nacionales 2026"
              />
            </Field>
            <Field label="Tipo" required help="Determina si aplica a todo el país, una autonomía o una localidad.">
              <Select value={kind} onChange={(e) => setKind(e.target.value)} required>
                {KIND_OPTIONS.map((o) => (
                  <option key={o.value} value={o.value}>{o.label}</option>
                ))}
              </Select>
            </Field>
          </div>
        </ModalBody>
        <ModalFooter>
          <Button variant="secondary" type="button" onClick={onClose} disabled={saving}>
            Cancelar
          </Button>
          <Button variant="primary" type="submit" disabled={saving || !name.trim()}>
            {saving && <Spinner size={12} color="#fff" style={{ marginRight: 6 }} />}
            {saving ? "Guardando…" : isEditing ? "Guardar cambios" : "Crear grupo"}
          </Button>
        </ModalFooter>
      </form>
    </Modal>
  );
}

function HolidayModal({ open, groupId, onClose, onCreated }) {
  const [date, setDate] = useState("");
  const [name, setName] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (open) {
      setDate("");
      setName("");
      setError(null);
    }
  }, [open]);

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!groupId) return;
    setSaving(true);
    setError(null);
    try {
      const r = await createHoliday(groupId, { date, name: name.trim() });
      onCreated(unwrap(r));
    } catch (err) {
      setError(typeof err === "string" ? err : err?.message || "No se pudo crear el festivo.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal open={open} onClose={onClose} title="Añadir festivo" size="md">
      <form onSubmit={onSubmit}>
        <ModalBody>
          {error && (
            <div style={{ marginBottom: 12 }}>
              <Alert tone="danger">{error}</Alert>
            </div>
          )}
          <div style={{ display: "grid", gridTemplateColumns: "180px 1fr", gap: 14 }}>
            <Field label="Fecha" required>
              <Input
                type="date"
                value={date}
                onChange={(e) => setDate(e.target.value)}
                required
              />
            </Field>
            <Field label="Nombre" required>
              <Input
                value={name}
                onChange={(e) => setName(e.target.value)}
                maxLength={255}
                required
                placeholder="Ej. Día de la Comunidad"
              />
            </Field>
          </div>
        </ModalBody>
        <ModalFooter>
          <Button variant="secondary" type="button" onClick={onClose} disabled={saving}>
            Cancelar
          </Button>
          <Button variant="primary" type="submit" disabled={saving || !date || !name.trim()}>
            {saving && <Spinner size={12} color="#fff" style={{ marginRight: 6 }} />}
            {saving ? "Añadiendo…" : "Añadir"}
          </Button>
        </ModalFooter>
      </form>
    </Modal>
  );
}

function ImportOfficialModal({ open, onClose, onImported }) {
  const toast = useToast();
  const currentYear = new Date().getFullYear();
  const initialYear = SUPPORTED_YEARS.includes(currentYear)
    ? currentYear
    : SUPPORTED_YEARS[SUPPORTED_YEARS.length - 1];

  const [presetId, setPresetId] = useState(SPANISH_PRESETS[0].id);
  const [year, setYear] = useState(initialYear);
  const [name, setName] = useState("");
  const [importing, setImporting] = useState(false);
  const [error, setError] = useState(null);

  const preset = useMemo(
    () => SPANISH_PRESETS.find((p) => p.id === presetId) || SPANISH_PRESETS[0],
    [presetId],
  );

  const preview = useMemo(
    () => preset.getHolidays(year),
    [preset, year],
  );

  useEffect(() => {
    if (open) {
      setPresetId(SPANISH_PRESETS[0].id);
      setYear(initialYear);
      setError(null);
    }
  }, [open, initialYear]);

  useEffect(() => {
    if (open) {
      setName(preset.defaultName(year));
    }
  }, [open, preset, year]);

  const onSubmit = async (e) => {
    e.preventDefault();
    setImporting(true);
    setError(null);
    try {
      const groupRes = await createHolidayGroup({ name: name.trim(), kind: preset.kind });
      const group = unwrap(groupRes);
      if (!group?.id) {
        throw new Error("No se pudo crear el grupo.");
      }

      const results = await Promise.allSettled(
        preview.map((h) => createHoliday(group.id, { date: h.date, name: h.name })),
      );
      const failed = results.filter((r) => r.status === "rejected");
      const okCount = results.length - failed.length;

      if (failed.length > 0 && okCount === 0) {
        throw new Error("No se pudo añadir ningún festivo. Revisa la conexión y vuelve a intentar.");
      }

      onImported(group);
      if (failed.length > 0) {
        toast.warning(
          "Importación parcial",
          `Se añadieron ${okCount} de ${preview.length} festivos. El resto puede que ya existieran.`,
        );
      } else {
        toast.success(
          "Festivos importados",
          `${okCount} festivos añadidos a "${group.name}".`,
        );
      }
    } catch (err) {
      setError(typeof err === "string" ? err : err?.message || "No se pudieron importar los festivos.");
    } finally {
      setImporting(false);
    }
  };

  return (
    <Modal open={open} onClose={onClose} title="Importar festivos oficiales" size="lg">
      <form onSubmit={onSubmit}>
        <ModalBody>
          {error && (
            <div style={{ marginBottom: 12 }}>
              <Alert tone="danger">{error}</Alert>
            </div>
          )}

          <div style={{ display: "grid", gridTemplateColumns: "1fr 140px", gap: 14, marginBottom: 14 }}>
            <Field label="Preset" required>
              <Select value={presetId} onChange={(e) => setPresetId(e.target.value)}>
                <optgroup label="Nacional">
                  {SPANISH_PRESETS.filter((p) => p.group === "national").map((p) => (
                    <option key={p.id} value={p.id}>{p.label}</option>
                  ))}
                </optgroup>
                <optgroup label="Autonómicos">
                  {SPANISH_PRESETS.filter((p) => p.group === "regional").map((p) => (
                    <option key={p.id} value={p.id}>{p.label}</option>
                  ))}
                </optgroup>
              </Select>
            </Field>
            <Field label="Año" required>
              <Select value={year} onChange={(e) => setYear(Number(e.target.value))}>
                {SUPPORTED_YEARS.map((y) => (
                  <option key={y} value={y}>{y}</option>
                ))}
              </Select>
            </Field>
          </div>

          <Field label="Nombre del grupo" required help="Puedes ajustarlo antes de importar.">
            <Input
              value={name}
              onChange={(e) => setName(e.target.value)}
              maxLength={255}
              required
            />
          </Field>

          {preset.group === "regional" && (
            <div style={{ marginTop: 12 }}>
              <Alert tone="info">
                Los presets autonómicos solo incluyen festividades de fecha fija. Si tu CCAA observa
                Jueves Santo o Lunes de Pascua, añádelos manualmente al grupo después de importar.
              </Alert>
            </div>
          )}

          <div
            style={{
              marginTop: 16,
              border: "1px solid var(--kr-border-subtle)",
              borderRadius: 8,
              overflow: "hidden",
            }}
          >
            <div
              style={{
                padding: "10px 14px",
                background: "var(--kr-neutral-50)",
                borderBottom: "1px solid var(--kr-border-subtle)",
                fontSize: 11,
                textTransform: "uppercase",
                letterSpacing: "0.04em",
                color: "var(--kr-fg-muted)",
                fontWeight: 600,
                display: "flex",
                justifyContent: "space-between",
              }}
            >
              <span>Festivos a importar</span>
              <span>{preview.length}</span>
            </div>
            <div style={{ maxHeight: 280, overflowY: "auto" }}>
              {preview.map((h) => (
                <div
                  key={h.date}
                  style={{
                    display: "grid",
                    gridTemplateColumns: "120px 1fr",
                    gap: 12,
                    padding: "8px 14px",
                    borderBottom: "1px solid var(--kr-border-subtle)",
                    fontSize: 13,
                  }}
                >
                  <span
                    style={{
                      fontFamily: "var(--kr-font-mono)",
                      color: "var(--kr-fg-strong)",
                      fontWeight: 600,
                    }}
                  >
                    {h.date}
                  </span>
                  <span style={{ color: "var(--kr-fg-strong)" }}>{h.name}</span>
                </div>
              ))}
            </div>
          </div>
        </ModalBody>
        <ModalFooter>
          <Button variant="secondary" type="button" onClick={onClose} disabled={importing}>
            Cancelar
          </Button>
          <Button variant="primary" type="submit" disabled={importing || !name.trim() || preview.length === 0}>
            {importing && <Spinner size={12} color="#fff" style={{ marginRight: 6 }} />}
            {importing ? "Importando…" : `Importar ${preview.length} festivos`}
          </Button>
        </ModalFooter>
      </form>
    </Modal>
  );
}

export default Festivos;
